import LightPopin from '@scripts/components/LightPopin';
import FormPartial from '@scripts/forms/FormPartial';
import HistoryForm from '@scripts/forms/HistoryForm';
import LabForm from '@scripts/forms/LabForm';
import ProfileForm from '@scripts/forms/ProfileForm';
import CvRiskHelpers from '@scripts/helpers';

/**
 * Global CVRISK form
 */
export default class {
  /**
   * @private
   *
   * @type {HTMLFormElement}
   */
  #element;

  /**
   * @private
   *
   * @type {LightPopin}
   */
  #popin;

  /**
   * If a submit button should be added to the popin content
   *
   * @private
   *
   * @type {bool}
   */
  #popinSubmit = false;

  /**
   * @private
   *
   * @type {ProfileForm}
   */
  #profile;

  /**
   * @private
   *
   * @type {HistoryForm}
   */
  #history;

  /**
   * @private
   *
   * @type {LabForm}
   */
  #lab;

  /**
   * Input used to detect if PDF file must be generated
   *
   * @private
   *
   * @type {?HTMLInputElement}
   */
  #inputPdf;

  /**
   * Error messages
   *
   * @private
   *
   * @type {Array<string>}
   */
  #errors = [];

  /**
   * Warning messages
   *
   * @private
   *
   * @type {Array<string>}
   */
  #warnings = [];

  /**
   * Success messages
   *
   * @private
   *
   * @type {Array<string>}
   */
  #success = [];

  /**
   * Currently being submitted
   *
   * @private
   *
   * @type {bool}
   */
  #processSubmit = false;

  constructor() {
    this.#element = document.getElementById('cvRiskForm');
    if (!this.#element) {
      throw new Error('CVRISK: form does not exist');
    }

    this.#popin = new LightPopin();
    this.#profile = new ProfileForm(this);
    this.#history = new HistoryForm(this);
    this.#lab = new LabForm(this);
    this.#inputPdf = this.#element.querySelector('#cvriskInputPdf');
  }

  /**
   * Handler for form submit
   *
   * @param {Event} e
   *
   * @return {void}
   */
  #handlerFormSubmit(e) {
    e.preventDefault();
    this.submit();
  }

  /**
   * Handler for form reset
   *
   * @param {Event} e
   *
   * @return {void}
   */
  #handlerFormReset(e) {
    e.preventDefault();
    this.reset();
  }

  /**
   * @return {Array<FormPartial>}
   */
  #getFormParts() {
    return [this.#profile, this.#history, this.#lab];
  }

  /**
   * Validate a form part
   *
   * @param {string} partial Form key: 'profile', 'history' or 'lab'
   *
   * @return {bool}
   */
  validatePartial(partial) {
    switch (partial) {
      case 'profile':
        return this.#profile.validate();
      case 'history':
        return this.#history.validate();
      case 'lab':
        return this.#lab.validate();
      default:
        return false;
    }
  }

  /**
   * Initialize form parts and event listeners
   *
   * @return {void}
   */
  initialize() {
    // Reset
    this.#element
      .querySelectorAll('.cvrisk-btn-reset')
      .forEach((btn) =>
        btn.addEventListener('click', (e) => this.#handlerFormReset(e))
      );

    // Submit
    this.#element
      .querySelectorAll('.cvrisk-btn-submit')
      .forEach((btn) =>
        btn.addEventListener('click', (e) => this.#handlerFormSubmit(e))
      );

    // Initialize form parts
    this.#getFormParts().forEach((form) => form.initialize());
  }

  /**
   * @return {bool}
   */
  hasAscvdRiskFactors() {
    let res = false;
    this.#getFormParts().forEach((form) => {
      if (form.hasAscvdRiskFactors()) {
        res = true;
        return;
      }
    });

    return res;
  }

  /**
   * Enable / Desable the generation of the PDF file
   *
   * @param {boolean} active If PDF must be generated (Default false)
   *
   * @return {void}
   */
  setPDF(active) {
    if (this.#inputPdf) {
      this.#inputPdf.value = active === true ? 1 : 0;
    }
  }

  /**
   * Submit the form or display a popin with error messages
   *
   * @return {void}
   */
  submit() {
    this.#processSubmit = true;

    // reset messages
    this.#errors = [];
    this.#warnings = [];
    this.#success = [];

    this.#getFormParts().every((form) => {
      form.validate();

      // errors
      this.#errors = [...this.#errors, ...form.errors];

      // warnings
      this.#warnings = [...this.#warnings, ...form.warnings];

      // success
      this.#success = [...this.#success, ...form.success];

      return this.#errors.length === 0;
    });

    const popinContent = CvRiskHelpers.buildPopinContent(
      '',
      this.#errors,
      this.#warnings,
      this.#success
    );

    if (popinContent) {
      this.openPopin(popinContent);
    } else {
      this.#element.submit();
    }

    this.#processSubmit = false;
  }

  /**
   * Reset each partial form
   *
   * @return {void}
   */
  reset() {
    this.#profile.reset();
    this.#history.reset();
    this.#lab.reset();
    this.setPDF(false);
  }

  /**
   * Attaches an event listener to the element
   *
   * @param {string} event Event name
   * @param {function} cb Callback function
   * @param {object} options Listener options
   *
   * @return {void}
   */
  on(event, cb, options = {}) {
    this.#element.addEventListener(event, cb, options);
  }

  /**
   * Detaches an event listener from the element
   *
   * @param {string} event Event name
   * @param {function} cb Callback function
   * @param {object} options Listener options
   *
   * @return {void}
   */
  off(event, cb, options = {}) {
    this.#element.removeEventListener(event, cb, options);
  }

  /**
   * Dispatch an event to the element
   *
   * @param {string} event Event name
   * @param {object} data Event data
   *
   * @return {void}
   */
  dispatch(event, data = {}) {
    this.#element.dispatchEvent(new CustomEvent(event, { detail: data }));
  }

  /**
   * Open the popin
   *
   * NOTICE: add a submit button if required
   *
   * @param {string} content Popin content
   *
   * @return {void}
   */
  openPopin(content) {
    if (this.#popinSubmit) {
      // success message
      content += CvRiskHelpers.buildAlertSuccess([
        CvRiskHelpers.success('can_submit'),
      ]).outerHTML;

      // submit button
      let wrapper = document.createElement('div');
      wrapper.classList.add(
        'cvrisk-mt-4',
        'cvrisk-flex',
        'cvrisk-items-center',
        'cvrisk-justify-center'
      );
      let submit = document.createElement('button');
      submit.id = 'cvriskPopinBtnSubmit';
      submit.classList.add('cvrisk-btn-submit-primary');
      submit.type = 'submit';
      submit.innerText = CvRiskHelpers.label('submit_continue') ?? 'Submit';
      submit.addEventListener('click', (e) => this.#element.submit());

      wrapper.appendChild(submit);
      content += wrapper.outerHTML;
    }

    this.#popin.open(content);

    if (this.#popinSubmit) {
      document
        .getElementById('cvriskPopinBtnSubmit')
        .addEventListener('click', (e) => this.#element.submit());

      // reset submit button
      this.#popinSubmit = false;
    }
  }

  /**
   * Download the PDF
   *
   * @return {void}
   */
  downloadPDF() {
    this.setPDF(true);
    this.#element.submit();
    this.setPDF(false);
  }

  /**
   * Get the form element
   *
   * @return {HTMLFormElement}
   */
  get element() {
    return this.#element;
  }

  /**
   * If the form is being submitted
   *
   * @return {bool}
   */
  get beingSubmitted() {
    return this.#processSubmit;
  }

  /**
   * Set whether a submit button should be added to the popin content
   *
   * @param {bool} value
   *
   * @return {void}
   */
  set withPopinSubmit(value = false) {
    this.#popinSubmit = value;
  }
}
